import moment from "moment";

/**
 * Formatage d'affichage d'une réservation à plat, telle que renvoyée par les
 * queries `myReservations` et `reservationsByOrderCode`.
 *
 * Fonctions pures, partagées par "Mes réservations" et la page d'annulation
 * par lien e-mail : dupliquées, les deux vues finiraient par présenter la même
 * réservation différemment.
 */

/**
 * Date au format JJ/MM/AAAA (offset-aware), "—" si pas de date.
 * Si le créneau est absent on retombe sur l'offset 0 (cohérent avec
 * reservationStatus).
 */
export function formatDate(reservation) {
    if (!reservation.date) {
        return "—";
    }
    const offset = (reservation.schedule && reservation.schedule.utcOffset) || 0;
    return moment(reservation.date).utcOffset(offset).locale("fr").format("DD/MM/YYYY");
}

/**
 * Horaire "HH:mm – HH:mm" : début = date (offset-aware, comme le panier
 * CartList.js:50), fin = début + durée du créneau (schedule.to - schedule.from).
 * schedule.from/to sont des heures UTC (cf. Activity.js qui applique utcOffset) ;
 * on raisonne en durée pour que la fin reste cohérente avec le début
 * offset-aware. Chaîne vide si pas de créneau.
 */
export function formatHoraire(reservation) {
    const schedule = reservation.schedule;
    if (!reservation.date || !schedule || !schedule.from || !schedule.to) {
        return "";
    }
    const toMinutes = (t) => {
        const parts = String(t).split(":");
        return parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
    };
    let duration = toMinutes(schedule.to) - toMinutes(schedule.from);
    if (duration < 0) {
        duration += 24 * 60;
    }
    const start = moment(reservation.date).utcOffset(schedule.utcOffset);
    return start.format("HH:mm") + " – " + start.clone().add(duration, "minutes").format("HH:mm");
}

/**
 * Libellé "Activité · JJ/MM/AAAA · HH:mm – HH:mm" pour le dialogue de
 * confirmation et les libellés d'accessibilité.
 */
export function describe(reservation) {
    const horaire = formatHoraire(reservation);
    return reservation.activityName + " · " + formatDate(reservation) + (horaire ? " · " + horaire : "");
}

// Messages d'erreur d'annulation. Le backend fait autorité : il renvoie un
// errorCode, l'UI ne fait que le traduire.
const CANCEL_ERRORS = {
    CANCELLATION_WINDOW_PASSED: "Le délai d'annulation est dépassé, veuillez contacter l'établissement.",
    ALREADY_CANCELLED: "Cette réservation est déjà annulée.",
    NOT_CANCELLABLE: "Cette réservation ne peut pas être annulée."
};
const CANCEL_ERROR_FALLBACK = "L'annulation a échoué. Veuillez réessayer plus tard.";

/**
 * Traduit un CancelReservationResult en message français, ou null si
 * l'annulation a réussi (ou qu'aucune n'a encore été tentée).
 */
export function cancelErrorMessage(result) {
    if (!result || result.success) {
        return null;
    }
    return CANCEL_ERRORS[result.errorCode] || CANCEL_ERROR_FALLBACK;
}
